import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type Chart,
  type ChartOptions,
  type Plugin,
} from "chart.js";
import { useIsDarkMode } from "~/hooks/useIsDarkMode";
import { cn } from "~/lib/utils";
import { EVAL_SCORE_MAX } from "~/services/evals";
import {
  EVAL_CHART_HEIGHT_CLS,
  TREND_AXIS_TEXT_COLOR,
  TREND_GRID_COLOR,
  TREND_LINE_COLOR,
  TREND_SPREAD_FILL,
} from "./constants/charts";
import type { TrendPoint } from "./selectors/runs";

// Registered at module load so the elements exist before the chart mounts
// (child chart effects run before a parent page effect would).
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

/** The rubric's floor. Scores never go below 1, so neither does the axis. */
const SCORE_AXIS_MIN = 1;

/**
 * The final mean, drawn beside the line. Passed per-chart rather than registered
 * globally, which would label every chart in the app. The mean is the last
 * dataset, so the index is read off the array; the first two are the spread band.
 */
const endpointLabelPlugin = (color: string): Plugin<"line"> => ({
  id: "evalScoreEndpointLabel",
  afterDatasetsDraw(chart: Chart<"line">) {
    const meanIndex = chart.data.datasets.length - 1;
    const last = chart.getDatasetMeta(meanIndex).data.at(-1);
    const value = chart.data.datasets[meanIndex]?.data.at(-1);
    if (!last || typeof value !== "number") {
      return;
    }

    const { ctx } = chart;
    ctx.save();
    ctx.font = "600 12px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = color;
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";
    ctx.fillText(value.toFixed(2), last.x - 8, last.y - 8);
    ctx.restore();
  },
});

const chartOptions = (
  tooltipTitles: string[],
  gridColor: string,
  axisTextColor: string,
  points: TrendPoint[]
): ChartOptions<"line"> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  // The hover target is the whole column, not the 8px dot: `index` mode with
  // `intersect: false` means anywhere in the plot reaches the nearest point.
  interaction: { mode: "index", intersect: false },
  plugins: {
    // The card's description names the line and the band, which is a direct
    // label; a legend box would restate it and add two swatches for what is one
    // measure and its range.
    legend: { display: false },
    tooltip: {
      displayColors: false,
      // The spread datasets exist to draw the band; repeating their values as
      // two more tooltip rows would say the same thing twice.
      filter: (item) => item.datasetIndex === 2,
      callbacks: {
        title: (items) => tooltipTitles[items[0].dataIndex],
        // Read values off the source point rather than the chart item: the
        // item's parsed value is nullable, and coercing a null there would
        // print a fabricated 0.
        label: (item) => {
          const point = points[item.dataIndex];
          const lines = [
            `Mean ${point.meanScore?.toFixed(2) ?? "—"} of ${EVAL_SCORE_MAX}`,
            `${Math.round(point.passRatePercent)}% of ${point.judged} judged passed`,
          ];
          if (point.minCaseScore != null && point.maxCaseScore != null) {
            lines.push(
              `Cases ranged ${point.minCaseScore.toFixed(2)}–${point.maxCaseScore.toFixed(2)}`
            );
          }
          return lines;
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { color: gridColor },
      ticks: {
        color: axisTextColor,
        font: { size: 11 },
        maxRotation: 0,
        autoSkipPadding: 16,
      },
    },
    y: {
      // The rubric's whole range, not the data's: a clipped axis would turn a
      // 0.1 wobble on a 1-4 scale into a cliff.
      min: SCORE_AXIS_MIN,
      max: EVAL_SCORE_MAX,
      ticks: { color: axisTextColor, font: { size: 11 }, stepSize: 1 },
      grid: { color: gridColor, tickLength: 0 },
      border: { display: false },
    },
  },
});

/**
 * Mean judge score across the recent runs, oldest left, with the spread of case
 * scores behind it. The band is what makes the mean worth plotting: a lone line
 * cannot separate a run where every case scored 3.2 from one where half scored 4
 * and half 2.4. Runs that judged nothing are absent rather than plotted as 0.
 */
export const ScoreTrend = ({ points }: { points: TrendPoint[] }) => {
  const isDark = useIsDarkMode();
  const mode = isDark ? "dark" : "light";
  const lineColor = TREND_LINE_COLOR[mode];
  const spreadFill = TREND_SPREAD_FILL[mode];
  const gridColor = TREND_GRID_COLOR[mode];
  const axisTextColor = TREND_AXIS_TEXT_COLOR[mode];
  // Rings the markers in the surface color so they stay legible where they sit
  // on the line or crowd each other.
  const surfaceColor = isDark ? "#212121" : "#ffffff";

  // One tick per day, not per run. The axis is run order, so dating every column
  // printed the same date twelve times and implied a spacing the runs never had.
  // The date marks where the day changes; the hover names the run to the minute.
  const dayLabels = points.map((point) =>
    new Date(point.startedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
  );
  const axisLabels = dayLabels.map((day, index) => (day === dayLabels[index - 1] ? "" : day));
  const tooltipTitles = points.map((point) => new Date(point.startedAt).toLocaleString());

  if (points.length < 2) {
    return (
      <div
        className={cn(
          EVAL_CHART_HEIGHT_CLS,
          "flex items-center justify-center text-sm text-muted-foreground"
        )}
      >
        {points.length === 0
          ? "No judged runs in this window yet."
          : "One judged run so far — a trend needs at least two."}
      </div>
    );
  }

  // The band is drawn as an upper bound filled down to a lower bound, so the two
  // bound datasets come first and the mean is drawn last, on top of the wash.
  const spreadDataset = (data: (number | null)[], fill: "+1" | false) => ({
    label: "Case score range",
    data,
    borderColor: "transparent",
    borderWidth: 0,
    pointRadius: 0,
    pointHoverRadius: 0,
    fill,
    backgroundColor: spreadFill,
    tension: 0.25,
  });

  return (
    // Height covers the plot plus the x-axis band, so the card never grows a
    // nested scrollbar to reach the tick labels.
    <div className={EVAL_CHART_HEIGHT_CLS}>
      <Line
        data={{
          labels: axisLabels,
          datasets: [
            spreadDataset(
              points.map((point) => point.maxCaseScore),
              "+1"
            ),
            spreadDataset(
              points.map((point) => point.minCaseScore),
              false
            ),
            {
              label: "Mean score",
              data: points.map((point) => point.meanScore),
              borderColor: lineColor,
              borderWidth: 2,
              tension: 0.25,
              fill: false,
              pointBackgroundColor: lineColor,
              pointBorderColor: surfaceColor,
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        }}
        options={chartOptions(tooltipTitles, gridColor, axisTextColor, points)}
        plugins={[endpointLabelPlugin(lineColor)]}
      />
    </div>
  );
};
